<script lang="ts">
	import type { Cartridge, Pistol, PistolFamily, PistolLight, PistolMapping, PistolModel } from 'endpoint/holster_line/configurator'
	import type { HolsterAccessoryAndFriends } from 'endpoint/holster_accessory/holster_accessory'
	import type { PistolSelectionInputs } from 'endpoint/pistol/pistol'
	import type { deserialize_from_address_bar } from 'shared/query_serialization'

	import Label from 'shared/component/StorefrontLabel.svelte'
	import Select from 'shared/component/Select.svelte'
	import { sort_pistol_makes, get_families, get_cartridges, get_pistol_models, get_pistol_lights, get_initial_values, availability_description, get_default_values } from 'lib/pistol_mapping_helpers'
	import { storefront_api2_prefix } from 'shared/var'
	import fetchy from 'shared/fetchy'
	import { onMount } from 'svelte'

	export let accessories: HolsterAccessoryAndFriends[]
	export let pistol_mapping: PistolMapping
	export let pistol_ref: number | null
	export let pistol_light_ref: number | null
	export let threaded = false
	export let holster_backer_ref: number | null
	export let pistol_magazine_bucket_ref: number | null
	export let holster_line: SingleJsonApiResponse<`holster_line`>
	export let initial_shopping_cart_values: ReturnType<typeof deserialize_from_address_bar>

	const {
		pistol_makes,
		pistol_make_ref_to_pistol_families_with_availability,
		pistol_family_ref_to_cartridges_to_pistols,
		pistol_ref_to_lights_to_holster_availabilities,
	} = pistol_mapping

	let selected_pistol_make: TrexPistolMake | null
	let selected_pistol_family: PistolFamily | null
	let selected_cartridge: Cartridge | null
	let selected_pistol_model_with_pistol: PistolModel | null
	let selected_light: PistolLight | null
	let selected_pistol: Pistol | null
	let pistol_lights: PistolLight[] | null

	const ops_api_fetch = fetchy(storefront_api2_prefix)

	onMount(async() => {
		({
			pistol_make: selected_pistol_make,
			pistol_family: selected_pistol_family,
			cartridge: selected_cartridge,
			// @ts-expect-error Generics aren't flowing through get_initial_values and get_default_values correctly
			pistol_model: selected_pistol_model_with_pistol,
			pistol_light: selected_light,
		} = {
			...(initial_shopping_cart_values.pistol_ref
				? get_initial_values({
					...pistol_mapping,
					...(await ops_api_fetch(`/pistol/${ initial_shopping_cart_values.pistol_ref }/configurator_inputs`)) as PistolSelectionInputs,
					...(typeof initial_shopping_cart_values.pistol_light_ref === `number` && { pistol_light_ref: initial_shopping_cart_values.pistol_light_ref }),
				})
				: get_default_values(pistol_mapping)
			),
		})

		threaded = !!initial_shopping_cart_values.threaded
	})

	const pistol_mag_carriers_exist = accessories.some(({ pistol_magazine_bucket }) => pistol_magazine_bucket)

	const backers_exist = accessories.some(({ holster_backer_ref }) => holster_backer_ref)

	const find_matching_item_by_property = <T extends object, P extends keyof T>(array: T[] | null, item: T | null, property: P) => array?.find(element => element[property] === item?.[property]) || null

	const on_pistol_makes = () => {
		selected_pistol_make = find_matching_item_by_property(pistol_makes, selected_pistol_make, `pistol_make_ref`) || null
	}

	$: pistol_makes && on_pistol_makes()
	$: sorted_pistol_makes = sort_pistol_makes(pistol_makes)

	$: pistol_families = get_families({
		pistol_make: selected_pistol_make,
		pistol_make_ref_to_pistol_families_with_availability,
	})

	const exactly_one_and_available_now = <T extends { available_now: boolean }>(options: T[] | null) => options?.length === 1 && options[0].available_now

	const on_pistol_families = (pistol_families: PistolFamily[] | null) => {
		selected_pistol_family = find_matching_item_by_property(pistol_families, selected_pistol_family, `pistol_family_ref`)
			|| (exactly_one_and_available_now(pistol_families) && pistol_families?.[0] || null)
	}
	$: on_pistol_families(pistol_families)

	$: cartridges = get_cartridges({
		pistol_family: selected_pistol_family,
		pistol_family_ref_to_cartridges_to_pistols,
	})

	const on_cartridges = (cartridges: Cartridge[] | null) => {
		selected_cartridge = find_matching_item_by_property(cartridges, selected_cartridge, `cartridge_ref`)
			|| (cartridges?.length === 1 ? cartridges[0] : null)
	}
	$: on_cartridges(cartridges)

	$: pistol_models = get_pistol_models(selected_cartridge)

	const on_pistol_models = (pistol_models: PistolModel[] | null) => {
		const matching_model = find_matching_item_by_property(pistol_models, selected_pistol_model_with_pistol, `pistol_model_ref`)
		const available_matching_model = matching_model?.available_now ? matching_model : null

		selected_pistol_model_with_pistol = available_matching_model
			|| (exactly_one_and_available_now(pistol_models) && pistol_models?.[0] || null)
			|| (backers_exist ? null : (pistol_models && pistol_models[0]))
	}

	$: on_pistol_models(pistol_models)

	$: pistol_lights = get_pistol_lights({
		pistol: selected_pistol_model_with_pistol?.pistol || null,
		pistol_ref_to_lights_to_holster_availabilities,
	})

	const on_selected_pistol_model_with_pistol = (selected_pistol_model_with_pistol: PistolModel | null) => {
		if (!selected_pistol_model_with_pistol) {
			selected_pistol = null
			return
		}

		const { pistol } = selected_pistol_model_with_pistol
		selected_pistol = pistol
	}

	$: on_selected_pistol_model_with_pistol(selected_pistol_model_with_pistol)

	const on_pistol_lights = (pistol_lights: PistolLight[] | null) => {
		selected_light = find_matching_item_by_property(pistol_lights, selected_light, `pistol_light_ref`) || pistol_lights?.[0] || null

		if (selected_light) {
			if (!selected_light.holster_availabilities.find(holster_availability => holster_availability.threaded_barrel === threaded)) {
				threaded = !threaded
			}
		}
	}

	$: on_pistol_lights(pistol_lights)

	$: threaded_and_not_threaded_options_available = selected_light?.holster_availabilities.some(({ threaded_barrel }) => threaded_barrel)
		&& selected_light?.holster_availabilities.some(({ threaded_barrel }) => !threaded_barrel)

	$: holster_backer_ref = selected_light?.holster_availabilities.find(({ threaded_barrel }) => threaded_barrel === threaded)!.holster_backer_ref || null

	$: pistol_ref = selected_pistol?.pistol_ref || null
	$: pistol_light_ref = selected_light?.pistol_light_ref || null
	$: pistol_magazine_bucket_ref = selected_light?.holster_availabilities[0].pistol_magazine_bucket_ref || null

</script>

{#if pistol_mag_carriers_exist || backers_exist}
	<tr>
		<th scope=row>
			<Label for="make-input">
				Make
			</Label>
		</th>
		<td>
			{#if pistol_makes.length === 1}
				<div class="single_value">
					{pistol_makes[0].name}
				</div>
			{:else if pistol_makes}
				<Select
					bind:value={selected_pistol_make}
					id=make-input
					placeholder='Select a make'
					autofocus
				>
					{#each sorted_pistol_makes as make (make.pistol_make_ref)}
						<option value={make}>{make.name}</option>
					{/each}
				</Select>
			{/if}
		</td>
	</tr>
	<tr>
		<th scope=row>
			<Label for="family-input">
				{selected_pistol_make?.name_for_family || `Family`}
			</Label>
		</th>
		<td>
			{#if exactly_one_and_available_now(pistol_families)}
				<div class="single_value">
					{pistol_families?.[0].name}
				</div>
			{:else if pistol_families}
				<Select
					bind:value={selected_pistol_family}
					id=family-input
					placeholder='Select a family'
				>
					{#each pistol_families as family (family.pistol_family_ref)}
						{#if family.available_now}
							<option value={family}>{family.name}</option>
						{:else}
							<option disabled>
								{family.name}
								{availability_description(family)}
							</option>
						{/if}
					{/each}
				</Select>
			{/if}
		</td>
	</tr>
	{#if holster_line.data.attributes.configurator_show_caliber_input}
		<tr>
			<th scope=row>
				<Label for="cartridge-input">
					Caliber
				</Label>
			</th>
			<td>
				{#if cartridges?.length === 1}
					<div class="single_value">
						{cartridges[0].name}
					</div>
				{:else if cartridges}
					<Select
						bind:value={selected_cartridge}
						id=cartridge-input
						placeholder='Select a caliber'
					>
						{#each cartridges as cartridge (cartridge.cartridge_ref)}
							<option value={cartridge}>{cartridge.name}</option>
						{/each}
					</Select>
				{/if}
			</td>
		</tr>
	{/if}
{/if}
{#if backers_exist}
	<tr>
		<th scope=row>
			<Label for="model-input">
				Model
			</Label>
		</th>
		<td>
			{#if exactly_one_and_available_now(pistol_models)}
				<div class="single_value">
					{pistol_models?.[0].name}
				</div>
			{:else if pistol_models}
				<Select
					bind:value={selected_pistol_model_with_pistol}
					id=model-input
					placeholder='Select a model'
				>
					{#each pistol_models as model (model.pistol_model_ref)}
						{#if model.available_now}
							<option value={model}>{model.name}</option>
						{:else}
							<option disabled>
								{model.name}
								{availability_description(model)}
							</option>
						{/if}
					{/each}
				</Select>
			{/if}
		</td>
	</tr>
	<tr>
		<th scope=row>
			<Label for="weaponlight-input">
				Weapon Light
			</Label>
		</th>
		<td>
			{#if pistol_lights?.length === 1}
				<div class="single_value">
					{pistol_lights[0].name}
				</div>
			{:else if pistol_lights}
				<Select bind:value={selected_light} id=weaponlight-input>
					{#each pistol_lights as light (light.pistol_light_ref)}
						{#if light.available_now}
							<option value={light}>{light.name}</option>
						{:else}
							<option disabled>
								{light.name}
								{availability_description(light)}
							</option>
						{/if}
					{/each}
				</Select>
			{/if}
		</td>
	</tr>

	{#if holster_line.data.attributes.configurator_allow_threaded_barrel}
		<tr>
			<th scope=row class="nowrap threaded_barrel_label">
				<Label
					for="threadedbarrel-input"
					disabled={!threaded_and_not_threaded_options_available}
				>
					Cover Threaded Barrel?
				</Label>
			</th>
			<td>
				<input
					type="checkbox"
					bind:checked={threaded}
					disabled={!threaded_and_not_threaded_options_available}
					id=threadedbarrel-input
				/>
			</td>
		</tr>
	{/if}
{/if}

<style>
	.single_value {
		font-size: 16px;
	}

	.nowrap {
		white-space: nowrap;
	}

	input:focus {
		box-shadow: 0 0 0 3px var(--trex-green);
		outline: none;
	}
	input[type=checkbox] {
		width: 1em;
		height: 1em;
		font-size: 2em;
	}

	@media (max-width: 450px) {
		.threaded_barrel_label {
			white-space: normal;
			width: 150px;
		}
	}
</style>
